import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

// Connects to data-controller="select"
export default class extends Controller {
  static values = {
    create: Boolean,
    maxItems: Number,
    noResults: String,
    placeholder: String,
    searchable: Boolean,
    sorted: { type: Boolean, default: true },
    title: String,
  }

  connect() {
    this.destroyTomSelect()
    this.initTomSelect()

    this.boundReconnect = this.reconnect.bind(this)
    this.boundDestroyTomSelect = this.destroyTomSelectBeforeCache.bind(this)
    window.addEventListener("turbo:morph", this.boundReconnect)
    document.addEventListener("turbo:before-cache", this.boundDestroyTomSelect)
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }

    window.removeEventListener("turbo:morph", this.boundReconnect)
    document.removeEventListener(
      "turbo:before-cache",
      this.boundDestroyTomSelect,
    )
  }

  reconnect() {
    if (!this.element.isConnected) return

    const currentValue = this.tomSelect?.getValue()

    this.destroyTomSelect()
    this.initTomSelect()

    if (currentValue !== undefined) {
      this.tomSelect.setValue(currentValue, true)
    }
  }

  initTomSelect() {
    this.tomSelect = new TomSelect(this.element, this.buildOptions())
    this.applyHideTextAttribute()
    this.setupMultiRowDetection()
    this.setupDialogScrollIntoView()
  }

  destroyTomSelect() {
    if (this.element.tomselect) {
      this.element.tomselect.destroy()
    }
    this.tomSelect = null
  }

  destroyTomSelectBeforeCache() {
    // Tom Select restores its initial options on destroy, so preserve the current selections for Turbo's cached snapshot.
    this.element.querySelectorAll("option").forEach((option) => {
      option.toggleAttribute("selected", option.selected)
    })
    const options = this.element.innerHTML

    this.destroyTomSelect()
    this.element.innerHTML = options
  }

  buildOptions() {
    const noResultsText = this.noResultsValue || "No results found"
    const placeholder = this.placeholderValue || "Select an option"
    const plugins = this.element.multiple ? this.multiPlugins() : null

    const options = {
      allowEmptyOption: true,
      hideSelected: false,
      maxOptions: null,
      placeholder: placeholder,
      plugins: plugins,
      render: {
        no_results: () => `<div class="no-results">${noResultsText}</div>`,
      },
      sortField: this.sortedValue
        ? [{ field: "text", direction: "asc" }]
        : [{ field: "$order" }],
    }

    // Clear search text after selecting an item in multi-select
    if (this.element.multiple) {
      options.onItemAdd = function () {
        this.setTextboxValue("")
        this.refreshOptions(false)
      }
    }

    // Limit number of items that can be selected
    if (this.hasMaxItemsValue && this.maxItemsValue > 0) {
      options.maxItems = this.maxItemsValue
    }

    if (!this.shouldEnableSearch()) {
      options.searchField = []
    }

    // Enable creating new items if explicitly set to true
    if (this.hasCreateValue && this.createValue === true) {
      options.create = true
      options.createFilter = (input) => {
        return input.length > 0
      }
    }

    return options
  }

  shouldEnableSearch() {
    return !this.hasSearchableValue || this.searchableValue
  }

  multiPlugins() {
    return {
      remove_button: {
        title: this.titleValue || "Remove this item",
        className: "ts-remove",
      },
    }
  }

  applyHideTextAttribute() {
    if (!this.shouldEnableSearch()) {
      this.tomSelect.wrapper.setAttribute("data-hide-search-text", "true")
      this.tomSelect.control.setAttribute(
        "data-placeholder",
        this.placeholderValue || "Select an option",
      )
    }
  }

  setupMultiRowDetection() {
    if (!this.element.multiple) return

    const checkMultiRow = () => {
      const control = this.tomSelect.control
      const items = control.querySelectorAll(".item")
      const input = control.querySelector("input")

      if (items.length === 0) {
        control.classList.remove("multi-row")
        return
      }

      const firstItemTop = items[0].offsetTop
      const lastItemTop = items[items.length - 1].offsetTop
      const inputTop = input ? input.offsetTop : firstItemTop

      if (lastItemTop > firstItemTop || inputTop > firstItemTop) {
        control.classList.add("multi-row")
      } else {
        control.classList.remove("multi-row")
      }
    }

    this.tomSelect.on("item_add", checkMultiRow)
    this.tomSelect.on("item_remove", checkMultiRow)

    this.resizeObserver = new ResizeObserver(checkMultiRow)
    this.resizeObserver.observe(this.tomSelect.control)

    checkMultiRow()
  }

  setupDialogScrollIntoView() {
    if (!this.element.closest("dialog")) return

    this.tomSelect.on("dropdown_open", () => {
      requestAnimationFrame(() => {
        this.tomSelect.dropdown.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        })
      })
    })
  }
}
